import React from "react";
import { Trans, WithTranslation, withTranslation } from "react-i18next";
import { Button, Form, Spinner, Stack } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import { DataService } from "../../services/DataService";
import { StyledModal } from "../modal/StyledModal";
import Datalist, { FooterOptions } from "../Datalist/Datalist";
import { IsUserSinfra, ReadFriendlyNumber } from "../../lib/utils";
import { SubmitKickbackReportModal } from "../modal/SubmitKickbackReportModal";
import UploadKickbackModal from "../modal/UploadKickbackModal";
import { Infobar } from "../Infobar/Infobar";
import { TKickback } from "../../services/API/Kickback";
import Field from "../fields/Field";
import {
  Department,
  DepartmentFunctionArgs,
} from "../../services/API/Departments";
import {
  calculateKickbackAmount,
  calculateKickbackDeduction,
  calculateReportedSumFromDeduction,
  formatKickbackPercent,
  hasKickbackCommentError,
} from "../../lib/KickbackMath";

function sortByMostCurrentMonth(values: string[]): string[] {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  const diffInMonths = (value: string): number => {
    const [yearStr, monthStr] = value.split("-");
    const year = Number(yearStr);
    const month = Number(monthStr);

    return Math.abs((year - currentYear) * 12 + (month - currentMonth));
  };

  return [...values].sort((a, b) => {
    const diffA = diffInMonths(a);
    const diffB = diffInMonths(b);

    if (diffA !== diffB) {
      return diffA - diffB;
    }

    return a.localeCompare(b);
  });
}

interface Props extends WithTranslation {
  invoiceReportInProgress: boolean | null;
}

interface State {
  error?: Error;
  kickbacks: TKickback[];
  AreInvoicesValid: boolean;
  isLoading: boolean;
  showSubmitModal: boolean;
  showSuccessModal: boolean;
  successMsg: string;
  showKickbackUploadModal: boolean;
  period: string;
  availablePeriods: string[];
  canReport: boolean;
  department: Department | undefined;
  invoiceSuccesfullyPulseToggle: boolean | null;
  focus: boolean;
}

class KickbackReport extends React.Component<Props, State> {
  private dd = React.createRef<HTMLDivElement>();
  private hasEverBeenInProgress = false;
  constructor(props: Props) {
    super(props);

    this.state = {
      kickbacks: [],
      AreInvoicesValid: false,
      isLoading: true,
      showSubmitModal: false,
      showSuccessModal: false,
      successMsg: "",
      showKickbackUploadModal: false,
      availablePeriods: [],
      canReport: false,
      period: "",
      department: undefined,
      invoiceSuccesfullyPulseToggle: false,
      focus: false,
    };
  }
  async componentDidMount() {
    if (!IsUserSinfra()) {
      await this.loadKickbackItems();
    } else {
      this.setState({
        isLoading: false,
      });
    }
  }

  async componentDidUpdate(prevProps: Readonly<Props>) {
    if (
      !prevProps.invoiceReportInProgress &&
      this.props.invoiceReportInProgress
    ) {
      this.hasEverBeenInProgress = true;
      this.focusDiv();
    }

    if (
      this.hasEverBeenInProgress &&
      prevProps.invoiceReportInProgress &&
      !this.props.invoiceReportInProgress
    ) {
      await this.loadKickbackItems();
      this.setState({ invoiceSuccesfullyPulseToggle: true });
      this.hasEverBeenInProgress = false;
    }
  }
  private getUserDepartmentPeriods = async (departmentId: number) => {
    const availablePeriods: string[] = [];

    const da: DepartmentFunctionArgs = {
      reportCheck: { departmentId, report: 4 },
    };

    const reportStatus =
      await DataService.Departments.CheckAlreadyReportedDepartment(da);

    Object.entries(reportStatus).forEach(([key, value]) => {
      if (value?.kickbackReport === true && value?.cantReport !== true) {
        availablePeriods.push(key);
      }
    });

    return availablePeriods;
  };

  private loadKickbackItems = async () => {
    this.setState({ isLoading: true });

    try {
      const [supplierCanReport, department] = await Promise.all([
        DataService.Kickback.CanActiveSupplierReport(),
        DataService.Departments.GetUserAssignedDepartments(),
      ]);

      let availablePeriods: string[] = [];
      let period = this.state.period;
      let kickbacks: any[] = [];

      const assignedDepartments = department.Content.response ?? [];
      const hasAssignedDepartments = assignedDepartments.length > 0;

      if (!supplierCanReport) {
        this.setState({
          kickbacks: [],
          availablePeriods: [],
          period: "",
          canReport: false,
          department: assignedDepartments[0] ?? undefined,
          isLoading: false,
        });

        return;
      }

      // 1. Hämta perioder som användaren faktiskt har rätt att rapportera på.
      if (hasAssignedDepartments) {
        const allPeriods = await Promise.all(
          assignedDepartments.map((dep) =>
            this.getUserDepartmentPeriods(dep.id),
          ),
        );

        availablePeriods = Array.from(
          new Set(
            allPeriods.reduce((acc, curr) => acc.concat(curr), [] as string[]),
          ),
        );
      } else {
        availablePeriods =
          await DataService.Kickback.GetPossibleKickbackPeriods();
      }

      if (availablePeriods.length === 0) {
        const effectiveCanReport = hasAssignedDepartments
          ? true
          : supplierCanReport;

        this.setState({
          kickbacks: [],
          availablePeriods: [],
          period: "",
          canReport: effectiveCanReport,
          department: assignedDepartments[0] ?? undefined,
          isLoading: false,
        });

        return;
      }

      // 2. Behåll endast rapporterbara perioder som faktiskt har fakturaunderlag.
      const kickbacksByPeriod = new Map<string, any[]>();
      const periodsWithInvoiceData: string[] = [];

      await Promise.all(
        availablePeriods.map(async (candidatePeriod) => {
          const year = candidatePeriod.slice(0, 4);
          const month = candidatePeriod.slice(5, 7);

          const candidateKickbacks =
            await DataService.Kickback.GetNewKickbackItems(year, month);

          if (candidateKickbacks.length > 0) {
            periodsWithInvoiceData.push(candidatePeriod);
            kickbacksByPeriod.set(candidatePeriod, candidateKickbacks);
          }
        }),
      );

      availablePeriods = sortByMostCurrentMonth(periodsWithInvoiceData);

      if (availablePeriods.length === 0) {
        const effectiveCanReport = hasAssignedDepartments
          ? true
          : supplierCanReport;

        this.setState({
          kickbacks: [],
          availablePeriods: [],
          period: "",
          canReport: effectiveCanReport,
          department: assignedDepartments[0] ?? undefined,
          isLoading: false,
        });

        return;
      }

      // 3. Behåll vald period om den fortfarande är giltig,
      // annars välj närmaste period.
      period =
        period && availablePeriods.includes(period)
          ? period
          : availablePeriods[0];

      kickbacks = kickbacksByPeriod.get(period) ?? [];

      // För användare med avdelning styr avdelningsperioderna rättigheten.
      const effectiveCanReport = hasAssignedDepartments
        ? true
        : supplierCanReport;

      this.setState({
        department: assignedDepartments[0] ?? undefined,
        kickbacks,
        availablePeriods,
        period,
        canReport: effectiveCanReport,
        isLoading: false,
      });
    } catch (error) {
      console.error("Fel i loadKickbackItems:", error);

      this.setState({
        error: error instanceof Error ? error : new Error("Okänt fel"),
        isLoading: false,
      });
    }
  };

  private onChangePeriod = async (period: string) => {
    this.setState({ isLoading: true });
    const year = period.slice(0, 4);
    const month = period.slice(5, 7);
    const kickbacks = await DataService.Kickback.GetNewKickbackItems(
      year,
      month,
    );

    this.setState({
      kickbacks,
      period,
      isLoading: false,
    });
  };

  private onSubmit = async () => {
    this.setState({
      showSubmitModal: false,
      showKickbackUploadModal: false,
      showSuccessModal: true,
      successMsg: this.props.t("kickback.sendSuccessful"),
    });
  };

  private opt = (period: string) => {
    const date = new Date(period);
    const text = `${date.toLocaleString("default", { month: "long" })} ${date
      .getFullYear()
      .toString()}`;
    return (
      <option
        key={period}
        style={{ textTransform: "capitalize" }}
        value={period}
      >
        {text}
      </option>
    );
  };

  private generateMonthOptions = () => {
    return this.state.availablePeriods.map((key) => {
      return this.opt(key);
    });
  };
  private activeDiv = () => {
    this.setState({ invoiceSuccesfullyPulseToggle: false });
  };
  private focusDiv = () => {
    if (this.dd.current) {
      this.dd.current?.focus();
      this.dd.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };
  private getInstructionTexts = () => {
    let texts = [];
    const key = "kickback.instructionText";
    let i = 1;
    let potentialText = this.props.t(key + i);
    while (potentialText != key + i) {
      texts.push(potentialText);
      i++;
      potentialText = this.props.t(key + i);
    }
    return texts;
  };
  private checkSumValidation = () => {
    return this.state.kickbacks.find((x: TKickback) =>
      hasKickbackCommentError(
        x.sumExclMoms,
        x.sumExclMomsChangesProps.value,
        x.comment,
      ),
    );
  };

  render() {
    const instructionTexts = this.getInstructionTexts();
    const { error, kickbacks } = this.state;
    console.log("isLoading:", this.state.isLoading);
    console.log("kickbacks:", this.state.kickbacks);
    console.log("availablePeriods:", this.state.availablePeriods);
    console.log("IsUserSinfra():", IsUserSinfra());
    if (error !== undefined) {
      return <div>{error.message}</div>;
    }

    return (
      <Stack
        style={{ backgroundColor: "#fffaf5" }}
        gap={1}
        className={
          !IsUserSinfra() && this.state.invoiceSuccesfullyPulseToggle
            ? "pulse"
            : "section kickback"
        }
        onMouseEnter={this.activeDiv}
      >
        <div ref={this.dd}>
          <h3>{this.props.t("kickback.title")}</h3>
        </div>
        {instructionTexts.length > 0 && (
          <ul>
            {instructionTexts.map((text) => (
              <li>
                <Trans>{text}</Trans>
              </li>
            ))}
          </ul>
        )}
        {this.state.isLoading && <Spinner />}
        {!this.state.canReport && !this.state.isLoading && !IsUserSinfra() && (
          <Infobar text={this.props.t("kickback.inactiveMsg")} />
        )}
        {this.props.invoiceReportInProgress &&
          !this.state.isLoading &&
          !IsUserSinfra() && (
            <Infobar
              text={this.props.t("kickback.stillProccessingStatistics")}
              useSpinner
            />
          )}
        {this.state.availablePeriods.length === 0 &&
          this.state.canReport &&
          !this.state.isLoading &&
          !IsUserSinfra() && (
            <Infobar text={this.props.t("kickback.noStatistics")} />
          )}
        {IsUserSinfra() && (
          <Infobar text={this.props.t("kickback.imitateUser")} />
        )}
        {this.state.canReport &&
          this.state.availablePeriods.length > 0 &&
          !IsUserSinfra() && (
          <Stack gap={3}>
            {this.state.canReport && (
              <Stack
                gap={3}
                direction="horizontal"
                style={{
                  justifyContent: "end",
                  flexWrap: "wrap",
                  alignItems: "flex-end",
                }}
              >
                <Form.Group>
                  <Form.Select
                    style={{
                      textTransform: "capitalize",
                      backgroundColor: "#f6f2ed",
                    }}
                    value={this.state.period}
                    onChange={(ev) => {
                      this.onChangePeriod(ev.target.value);
                    }}
                  >
                    {this.generateMonthOptions()}
                  </Form.Select>
                </Form.Group>
                {this.state.kickbacks.length > 0 && (
                  <Button
                    style={{ color: "rgb(23, 61, 84)" }}
                    disabled={
                      this.state.kickbacks.some(
                        (c) => c.sumExclMomsChangesProps.validation == "Error",
                      ) || typeof this.checkSumValidation() !== "undefined"
                    }
                    className="btn button-submit"
                    onClick={() => {
                      this.setState({ showSubmitModal: true });
                    }}
                  >
                    <>
                      <FontAwesomeIcon
                        icon={faCheck}
                        style={{ marginRight: 8 }}
                      />
                      {this.props.t("kickback.markPeriodAsDone")}
                    </>
                  </Button>
                )}
              </Stack>
            )}

            {this.state.kickbacks.length > 0 && (
              <div style={{ maxHeight: "60vh" }}>
                <Datalist
                  items={kickbacks}
                  isLoading={this.state.isLoading}
                  uniqueKey="contractId"
                  columns={[
                    {
                      fieldName: "contractId",
                      isActive: true,
                      key: "contractId",
                      minWidth: 100,
                      name: this.props.t("general.contract"),
                      onRender(item, col) {
                        return `${item.contractId} - ${item.contractType} - ${item.contractName}`;
                      },
                      footer: FooterOptions.COUNT,
                      disabled: true,
                    },
                    {
                      fieldName: "sumExclMoms",
                      isActive: true,
                      key: "sumExclMoms",
                      minWidth: 60,
                      name: this.props.t(
                        "kickbackReportPage.table.header.sumExclMoms",
                      ),
                      onRender: (item) => {
                        return ReadFriendlyNumber(item.sumExclMoms);
                      },
                      footer: FooterOptions.SUM,
                      disabled: true,
                    },
                    {
                      fieldName: "sum",
                      isActive: true,
                      key: "sum",
                      minWidth: 60,
                      customCellPadding: "1px",
                      name: this.props.t(
                        "kickbackReportPage.table.header.sumExclMomsDeduction",
                      ),
                      onRender: (item, col) => {
                        return (
                          <Field
                            property={item.sumExclMomsChangesProps}
                            onChange={(val) => {
                              const newValidatedValue =
                                DataService.Kickback.ValidateSum(val);
                              const kickbacks = [...this.state.kickbacks];
                              const index = kickbacks.findIndex(
                                (cont) => cont.contractId == item.contractId,
                              );
                              kickbacks.splice(index, 1, {
                                ...kickbacks[index],
                                sumExclMomsChangesProps: newValidatedValue,
                              });
                              this.setState({ kickbacks: kickbacks });
                            }}
                          />
                        );
                      },
                      footer: (items) => {
                        const totalSum = items.reduce((prev, curr) => {
                          const val = calculateKickbackDeduction(
                            curr.sumExclMoms,
                            calculateReportedSumFromDeduction(
                              curr.sumExclMoms,
                              curr.sumExclMomsChangesProps.value,
                            ),
                          );
                          return prev + val;
                        }, 0);
                        return ReadFriendlyNumber(totalSum);
                      },
                    },
                    {
                      fieldName: "reportedSum",
                      isActive: true,
                      key: "reportedSum",
                      minWidth: 60,
                      name: this.props.t(
                        "kickbackReportPage.table.header.sumExclMomsReported",
                      ),
                      onRender: (item) => {
                        return ReadFriendlyNumber(
                          calculateReportedSumFromDeduction(
                            item.sumExclMoms,
                            item.sumExclMomsChangesProps.value,
                          ),
                        );
                      },
                      footer: (items) => {
                        const totalReported = items.reduce((prev, curr) => {
                          return (
                            prev +
                            calculateReportedSumFromDeduction(
                              curr.sumExclMoms,
                              curr.sumExclMomsChangesProps.value,
                            )
                          );
                        }, 0);
                        return ReadFriendlyNumber(totalReported);
                      },
                      disabled: true,
                    },
                    {
                      fieldName: "kickbackProcent",
                      isActive: true,
                      key: "kickbackProcent",
                      minWidth: 60,
                      name: this.props.t(
                        "kickbackReportPage.table.header.kickbackProcent",
                      ),
                      onRender: (item, col) => {
                        return formatKickbackPercent(item.kickbackProcent);
                      },
                      disabled: true,
                    },
                    {
                      fieldName: "calculated0",
                      isActive: true,
                      key: "calculated0",
                      minWidth: 60,
                      name: this.props.t(
                        "kickbackReportPage.table.header.calculatedKickback",
                      ),
                      onRender: (item, col) => {
                        if (item.sumExclMomsChangesProps.validation == "Error")
                          return "0,00";
                        return ReadFriendlyNumber(
                          calculateKickbackAmount(
                            calculateReportedSumFromDeduction(
                              item.sumExclMoms,
                              item.sumExclMomsChangesProps.value,
                            ),
                            item.kickbackProcent,
                          ),
                        );
                      },
                      footer: (items) => {
                        const kickbackTotal = items.reduce((prev, curr) => {
                          return (
                            prev +
                            calculateKickbackAmount(
                              calculateReportedSumFromDeduction(
                                curr.sumExclMoms,
                                curr.sumExclMomsChangesProps.value,
                              ),
                              curr.kickbackProcent,
                            )
                          );
                        }, 0);
                        return ReadFriendlyNumber(kickbackTotal);
                      },
                      disabled: true,
                    },
                    {
                      fieldName: "comment",
                      isActive: true,
                      key: "comment",
                      minWidth: 60,
                      customCellPadding: "1px",
                      name: this.props.t(
                        "kickbackReportPage.table.header.commentChanges",
                      ),
                      onRender: (item, col) => {
                        const parsedChangeValue =
                          calculateReportedSumFromDeduction(
                            item.sumExclMoms,
                            item.sumExclMomsChangesProps.value,
                          );
                        const checkIfSumsDontMatch =
                          item.sumExclMoms.toFixed(2) !==
                            parsedChangeValue.toFixed(2) &&
                          item.comment.length == 0;
                        return (
                          <Field
                            property={{
                              type: "Text",
                              key: "comment",
                              value: item.comment,
                              format: this.props.t(
                                "kickback.commentPlaceholder",
                              ),
                              message: this.props.t("kickback.commentChanges"),
                              validation: checkIfSumsDontMatch
                                ? "Error"
                                : "Good",
                            }}
                            onChange={(val) => {
                              const kickbacks = [...this.state.kickbacks];
                              const index = kickbacks.findIndex(
                                (cont) => cont.contractId == item.contractId,
                              );
                              kickbacks.splice(index, 1, {
                                ...kickbacks[index],
                                comment: val,
                              });
                              this.setState({ kickbacks: kickbacks });
                            }}
                          />
                        );
                      },
                    },
                  ]}
                  noItemsText={this.props.t("kickback.table.empty")}
                  initialSortKey="contractId"
                  setColumns={(cols) => {}}
                />
              </div>
            )}
          </Stack>
        )}
        {this.state.showSubmitModal && (
          <SubmitKickbackReportModal
            disabled={false}
            kickbacks={this.state.kickbacks}
            onDismiss={() => {
              this.setState({ showSubmitModal: false });
            }}
            onSubmitted={this.onSubmit}
            period={this.state.period}
          />
        )}
        {this.state.showSuccessModal && (
          <StyledModal
            headerStyle={{ backgroundColor: "#173c53", color: "white" }}
            onDismiss={() => {
              this.setState({ showSuccessModal: false });
            }}
            title={this.props.t("invoiceReport.successModalTitle") as string}
            body={
              <Stack gap={3} style={{ alignItems: "center" }}>
                <div style={{ fontSize: 18, fontWeight: 500 }}>
                  {this.state.successMsg}
                </div>
                <Button
                  style={{ width: 100 }}
                  className="button button-cancel"
                  onClick={() => {
                    window.location.reload();
                  }}
                >
                  {this.props.t("general.close")}
                </Button>
              </Stack>
            }
          />
        )}
        {this.state.showKickbackUploadModal && (
          <UploadKickbackModal
            onDismiss={() => {
              this.setState({ showKickbackUploadModal: false });
            }}
            onSubmitted={this.onSubmit}
          />
        )}
      </Stack>
    );
  }
}

export default withTranslation()(KickbackReport);
